'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, stack, vstack } from '@flipster/styled-system/patterns';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';

import { positionCloseModalBunja } from '../state';

interface Props {
  showOnlyFullMessage?: boolean;
}

export const FullCloseDetail = ({ showOnlyFullMessage = false }: Props) => {
  const t = useTranslations();
  const { positionAtom, amountAtom, instrumentAtom } = useBunja(positionCloseModalBunja);

  const position = useAtomValue(positionAtom);
  const amount = useAtomValue(amountAtom);
  const instrument = useAtomValue(instrumentAtom);
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

  return (
    <div
      className={stack({
        p: 16,
        borderRadius: 8,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
      })}
    >
      <div className={flex({ justify: 'space-between', align: 'center' })}>
        <div
          className={css({
            textStyle: 'Text/L/Strong',
            color: 'content.default.level-5',
          })}
        >
          {t('Position_close.r251111.Full_quantity')}
        </div>
        <div className={vstack({ gap: 4, alignItems: 'flex-end' })}>
          <div className={css({ textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-5' })}>
            <Formatted.Number
              value={position.quantity}
              abs
              precision={instrument.tradeQtyPrecision}
              suffix={instrument.positionUnit}
              toFixed
            />
          </div>
          <div className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' })}>
            <Formatted.Number
              value={amount}
              abs
              precision={quoteCurrencyPrecision}
              prefix='≈ '
              suffix={instrument.quoteCurrency}
              toFixed
            />
          </div>
        </div>
      </div>
      {showOnlyFullMessage && (
        <p
          className={css({
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderColor: 'border.default.level-1',
            pt: 16,
            mt: 16,
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-2',
          })}
        >
          {t('Trade.r251119.This_is_minimum_closable_amount_can_only_close')}
        </p>
      )}
    </div>
  );
};
